import { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { certExpiryLabel, domainErrorMessage } from '@/lib/domainForm';
import type { DomainDetail, DomainEndpointStatus } from '@/types/domain';

const TRAEFIK_SNIPPET = `services:
  traefik:
    # ...existing image, ports, labels...
    command:
      # ...existing flags...
      - --providers.file.directory=/etc/traefik/dynamic
      - --providers.file.watch=true
    volumes:
      # ...existing volumes...
      - bridge-tls:/etc/traefik/dynamic:ro

volumes:
  bridge-tls:
    external: true`;

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

/** Whether one endpoint serves this domain's current certificate. */
function endpointBadge(ep: DomainEndpointStatus, hasCert: boolean): { label: string; variant: BadgeVariant } {
  if (ep.status === 'error') return { label: 'Error', variant: 'destructive' };
  if (ep.status === 'not_configured') return { label: 'Not configured', variant: 'outline' };
  if (!hasCert) return { label: 'No certificate', variant: 'outline' };
  return ep.ready ? { label: 'Ready', variant: 'default' } : { label: 'Outdated', variant: 'secondary' };
}

function endpointDetail(ep: DomainEndpointStatus, hasCert: boolean): string {
  if (ep.error) return ep.error;
  if (ep.status !== 'ready') return '-';
  if (!hasCert) return 'File provider mounted; no certificate for this domain yet.';
  return ep.ready ? '-' : "Doesn't hold this certificate yet; push pending.";
}

const textareaClass =
  'font-mono text-xs w-full min-h-[140px] rounded-md border border-input bg-transparent px-3 py-2 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const formatDateTime = (s: string | null) => (s ? new Date(s).toLocaleString() : '-');

function PemField({ id, label, value, onChange, placeholder }: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium">{label}</label>
        <input
          type="file"
          accept=".pem,.crt,.key,.txt"
          aria-label={`${label} file`}
          className="max-w-[220px] text-xs"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file) onChange(await file.text());
          }}
        />
      </div>
      <textarea id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={textareaClass} spellCheck={false} />
    </div>
  );
}

interface OriginCertCardProps {
  domain: DomainDetail;
  /** The certificate was uploaded or removed. */
  onChanged: () => void;
  /** Reload the domains (endpoint status changes after a push). */
  onRefresh: () => void;
  refreshing: boolean;
}

/** One domain's Cloudflare origin certificate and which Traefik endpoints serve it. */
export function OriginCertCard({ domain, onChanged, onRefresh, refreshing }: OriginCertCardProps) {
  const ids = useId();
  const [certPem, setCertPem] = useState('');
  const [keyPem, setKeyPem] = useState('');
  const [busy, setBusy] = useState<'cert' | 'push' | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const removeBusyRef = useRef(false);
  const [copied, setCopied] = useState(false);
  // Delayed refreshes and the "Copied" reset; cleared when the card unmounts (domain switched or tab left).
  const timers = useRef<Set<number>>(new Set());
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((t) => window.clearTimeout(t));
      pending.clear();
    };
  }, []);
  const later = (fn: () => void, ms: number) => {
    const t = window.setTimeout(() => {
      timers.current.delete(t);
      fn();
    }, ms);
    timers.current.add(t);
  };

  const cert = domain.certificate;
  const expiry = certExpiryLabel(cert);
  const removeBlocked =
    domain.new_app_tls_mode === 'origin' ? `New apps on ${domain.name} use origin TLS; switch them to Let's Encrypt first.` : null;

  const saveCert = async () => {
    setBusy('cert');
    setMessage(null);
    try {
      await api.domains.uploadOriginCert(domain.id, { cert_pem: certPem, key_pem: keyPem });
      onChanged();
      setCertPem('');
      setKeyPem('');
      setMessage({ kind: 'ok', text: 'Certificate saved and pushed to Traefik endpoints. Status below refreshes in a few seconds.' });
      later(onRefresh, 3000);
    } catch (err) {
      setMessage({ kind: 'error', text: domainErrorMessage(err, 'Failed to save certificate') });
    } finally {
      setBusy(null);
    }
  };

  const pushNow = async () => {
    setBusy('push');
    setMessage(null);
    try {
      await api.settings.tls.push();
      setMessage({ kind: 'ok', text: 'Push started.' });
      later(onRefresh, 3000);
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof Error && err.message ? err.message : 'Push failed' });
    } finally {
      setBusy(null);
    }
  };

  const removeCert = async () => {
    if (removeBusyRef.current) return;
    removeBusyRef.current = true;
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      await api.domains.deleteOriginCert(domain.id);
      onChanged();
      setRemoveOpen(false);
      toast.success('Certificate removed');
    } catch (err) {
      setRemoveError(domainErrorMessage(err, 'Failed to remove certificate'));
    } finally {
      removeBusyRef.current = false;
      setRemoveBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {message && (
        <div
          role={message.kind === 'ok' ? 'status' : 'alert'}
          className={`rounded-md px-4 py-2 text-sm [overflow-wrap:anywhere] ${message.kind === 'ok' ? 'bg-status-success-soft text-status-success' : 'bg-status-danger-soft text-status-danger'}`}
        >
          {message.text}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Origin certificate for {domain.name}</CardTitle>
          <CardDescription>
            Create it in Cloudflare (SSL/TLS → Origin Server) for <code>*.{domain.name}</code>. The private key is stored
            encrypted and is never shown again.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {cert ? (
            <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
              <dt className="text-muted-foreground">Hostnames</dt>
              <dd className="font-mono break-all">{cert.sans.join(', ')}</dd>
              <dt className="text-muted-foreground">Issuer</dt>
              <dd className="break-all">{cert.issuer}</dd>
              <dt className="text-muted-foreground">Valid</dt>
              <dd className="flex flex-wrap items-center gap-2">
                {formatDateTime(cert.not_before)} → {formatDateTime(cert.not_after)}
                {cert.expires_in_days < 30 && expiry && <Badge variant="destructive">{expiry}</Badge>}
              </dd>
              <dt className="text-muted-foreground">SHA-256</dt>
              <dd className="font-mono text-xs break-all">{cert.fingerprint}</dd>
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">No origin certificate uploaded.</p>
          )}
          <div className="grid gap-4 lg:grid-cols-2">
            <PemField id={`${ids}-cert`} label={cert ? 'Replace certificate (PEM)' : 'Certificate (PEM)'} value={certPem} onChange={setCertPem} placeholder="-----BEGIN CERTIFICATE-----" />
            <PemField id={`${ids}-key`} label="Private key (PEM)" value={keyPem} onChange={setKeyPem} placeholder="-----BEGIN PRIVATE KEY-----" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => void saveCert()} disabled={busy !== null || !certPem.trim() || !keyPem.trim()}>
              {busy === 'cert' ? 'Validating…' : 'Save certificate'}
            </Button>
            {cert ? (
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive"
                disabled={busy !== null || removeBlocked !== null}
                title={removeBlocked ?? undefined}
                aria-describedby={removeBlocked ? `${ids}-remove-hint` : undefined}
                onClick={() => {
                  setRemoveError(null);
                  setRemoveOpen(true);
                }}
              >
                Remove certificate
              </Button>
            ) : null}
          </div>
          {cert && removeBlocked ? (
            <p id={`${ids}-remove-hint`} className="text-xs text-muted-foreground">
              {removeBlocked}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
          <div className="min-w-0 space-y-1.5">
            <CardTitle>Endpoints</CardTitle>
            <CardDescription>
              Traefik endpoints and whether they serve this certificate. Checked regularly and after every certificate change.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={busy !== null || refreshing}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
            <Button size="sm" onClick={() => void pushNow()} disabled={busy !== null || !cert}>
              {busy === 'push' ? 'Pushing…' : 'Push now'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {domain.endpoints.length === 0 ? (
            <p className="text-sm text-muted-foreground">No endpoint has a traefik-router stack yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {domain.endpoints.map((ep) => {
                  const b = endpointBadge(ep, cert !== null);
                  return (
                    <TableRow key={ep.endpoint_id}>
                      <TableCell className="font-medium">{ep.name}</TableCell>
                      <TableCell><Badge variant={b.variant}>{b.label}</Badge></TableCell>
                      <TableCell className="max-w-[360px] text-sm whitespace-normal text-muted-foreground">{endpointDetail(ep, cert !== null)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>One-time setup</CardTitle>
          <CardDescription>Before giving new apps on any domain origin TLS.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <ol className="list-decimal space-y-2 pl-5">
            <li>Cloudflare dashboard → SSL/TLS → set encryption mode to <strong>Full (strict)</strong> for the zone.</li>
            <li>
              Add the file provider and the <code>bridge-tls</code> volume to each endpoint's <code>traefik-router</code>{' '}
              stack and redeploy it. The bridge creates the volume and writes every domain's certificate into it.
            </li>
            <li>
              Proxied apps inherit Cloudflare limits: 100 MB request bodies (Free/Pro), 100 s response timeout,
              proxy-supported ports only. WebSockets work.
            </li>
          </ol>
          <div className="relative">
            <pre className="overflow-x-auto rounded-md bg-muted p-4 text-xs"><code>{TRAEFIK_SNIPPET}</code></pre>
            <Button
              size="sm"
              variant="outline"
              className="absolute right-2 top-2"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(TRAEFIK_SNIPPET);
                  setCopied(true);
                  later(() => setCopied(false), 2000);
                } catch {
                  // Clipboard unavailable (insecure context); the snippet stays selectable.
                }
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={removeOpen}
        onOpenChange={(next) => {
          if (!next && !removeBusyRef.current) {
            setRemoveOpen(false);
            setRemoveError(null);
          }
        }}
        title="Remove certificate?"
        description={`Remove the origin certificate of ${domain.name}? Endpoints stop serving it after the next push.`}
        confirmLabel={removeBusy ? 'Removing…' : 'Remove'}
        destructive
        loading={removeBusy}
        error={removeError}
        onConfirm={() => void removeCert()}
      />
    </div>
  );
}
